import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient, createAbortableConfig } from '@src/api/apiClient/axiosClient';
import { Pagination } from '@src/api/types/pagination';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { ChatChangeType } from '@src/enum/chatChangeTypeEnum';
import { ChatSearchSortOrder } from '@src/enum/chatSearchSortOrder';
import { ChatReportReasonEnum } from '@src/features/shared/community/enums/chatReportReason';

import { ChatReaction } from './chatReactionApiClient';

type User = {
  user_id: number;
  display_name?: string;
  avatar_url?: string;
  user_type?: ChannelMemberUserTypeEnum;
};

export type ChatAttachment = {
  id: number;
  file_name: string;
  extension: string;
  file_url: string;
  thumbnail_url?: string | null;
  blob?: Blob | File;
};

export type LinkPreviewResponse = {
  url: string;
  siteName: string;
  title: string;
  description: string;
  image?: string;
};

export interface ReplyUser {
  display_name?: string;
  avatar_url?: string;
}

export type ChatThreadSummary = {
  users: ReplyUser[];
  count: number;
  latest_created_at: string;
};

export interface Chat extends User {
  id: number;
  message: string;
  is_edited: boolean;
  is_trash_displayed: boolean;
  is_anonymous: boolean;
  is_link_preview_hidden?: boolean;
  created_at: string;
  date_label: string;
  user_type: ChannelMemberUserTypeEnum;
  user_id: number;
  can_click: boolean;
  channel_member_id: number;
  attachments?: ChatAttachment[];
  updated_at?: string;
  reactions?: ChatReaction[];
  threads?: ChatThreadSummary;
  preview?: LinkPreviewResponse;
}

export type PinnedChat = {
  id: number;
  message: string;
  is_edited: boolean;
  user_type: ChannelMemberUserTypeEnum;
  user_id: number;
  created_at: string;
  display_name?: string;
  avatar_url?: string;
  can_click: boolean;
  channel_member_id: number;
  reactions?: ChatReaction[];
  parent_chat_id?: number;
};

export type ChatGroup = {
  date_label: string;
  chats: Chat[];
};

interface Channel {
  id: number;
  name: string;
  code: string;
}

export type ChatSearch = {
  id: number;
  parent_chat_id?: number;
  message: string;
  user: User;
  channel: Channel;
  attachments: ChatAttachment[];
  date_label: string;
  is_edited: number;
  created_at: string;
  updated_at: string;
};

export type ChatIndexRequest = {
  page?: number | string;
  after_id?: number;
  is_polling?: boolean;
  load_around_unread?: boolean;
  mark_as_read?: boolean;
};

export type ChatIndexResponse = {
  data: ChatGroup[];
  pagination: Pagination;
  is_access_denied?: boolean;
  first_unread_chat_id: number | null;
};

export interface ThreadChat extends User {
  id: number;
  message: string;
  is_edited: boolean;
  is_trash_displayed: boolean;
  is_anonymous: boolean;
  is_link_preview_hidden?: boolean;
  created_at: string;
  can_click: boolean;
  channel_member_id: number;
  attachments?: ChatAttachment[];
  reactions?: ChatReaction[];
  preview?: LinkPreviewResponse;
}

export interface ChatThreadIndexResponse extends ThreadChat {
  threads: ThreadChat[];
  first_unread_thread_chat_id: number | null;
}

export type ChatAttachmentId = {
  chat_attachment_id: number;
};

export type ChatCreateRequest = {
  message: string;
  attachments?: ChatAttachmentId[];
  is_anonymous?: boolean;
  parent_chat_id?: number;
};

export type ChatUpdateRequest = {
  message: string;
  attachments?: ChatAttachmentId[];
  is_anonymous?: boolean;
};

export type ChatDeleteRequest = unknown;

export type ChatCreateResponse = unknown;

export type ChatUpdateResponse = unknown;

export type ChatDeleteResponse = unknown;

export type ChatPageResponse = {
  page: number;
};

export type ChatCreateErrorResponse = {
  message: string[];
  attachments: string[];
};

export type ChatUpdateErrorResponse = {
  message: string[];
  attachments?: string[];
};

export type ChatDeleteErrorResponse = {
  message: string[];
  attachments?: string[];
};

export type UploadFileResponse = {
  id: number;
  file_name: string;
  extension: string;
  file_url: string;
  thumbnail_url?: string | null;
};
export type UploadFileErrorResponse = {
  file: string[];
};

export type DeleteFileRequest = {
  file_url: string;
};
export type DeleteFileResponse = unknown;
export type DeleteFileErrorResponse = {
  file_url: string;
};

export type ChatChange = {
  id: number;
  message: string;
  type: ChatChangeType;
  reactions?: ChatReaction[];
  attachments?: ChatAttachment[];
  updated_at?: string;
  deleted_at?: string;
  threads?: ChatThreadSummary;
  preview?: LinkPreviewResponse;
};

export type ChatChangesResponse = {
  data: ChatChange[];
  latest_timestamp: string;
};

export type ChatSearchRequest = {
  keyword: string;
  channel_codes?: string[];
  sort_order?: ChatSearchSortOrder;
  page?: number | string;
  per_page?: number | string;
};

export type ChatSearchResponse = {
  pagination: Pagination;
  data: ChatSearch[];
};

export type ChatReportRequest = {
  reason: ChatReportReasonEnum;
};

export type ChatReportResponse = unknown;

export type ChatReportErrorResponse = {
  reason: string[];
};

export type ChatContextResponse = {
  parent_chat_id: number;
  thread_chat_id?: number;
  thread_has_mention: boolean;
};

export const chatApiClient = {
  index: async (
    channelCode: string,
    params: ChatIndexRequest = {},
    signal?: AbortSignal,
  ): Promise<ApiResult<ChatIndexResponse>> => {
    return await axiosClient.get(`/community/channels/${channelCode}/chats`, {
      params,
      ...createAbortableConfig(signal),
    });
  },
  threads: async (
    channelCode: string,
    chatId: number,
    params?: {
      mark_as_read?: boolean;
    },
  ): Promise<ApiResult<ChatThreadIndexResponse>> => {
    return await axiosClient.get(`/community/channels/${channelCode}/chats/${chatId}/threads`, {
      params,
    });
  },
  create: async (
    channelCode: string,
    params: ChatCreateRequest,
  ): Promise<ApiResult<ChatCreateResponse, ChatCreateErrorResponse>> => {
    return await axiosClient.post(`/community/channels/${channelCode}/chats`, { chat: params });
  },
  update: async (
    channelCode: string,
    id: number,
    params: ChatUpdateRequest,
  ): Promise<ApiResult<ChatUpdateResponse, ChatUpdateErrorResponse>> => {
    return await axiosClient.put(`/community/channels/${channelCode}/chats/${id}`, {
      chat: params,
    });
  },
  delete: async (
    channelCode: string,
    id: number,
  ): Promise<ApiResult<ChatDeleteResponse, ChatDeleteErrorResponse>> => {
    return await axiosClient.delete(`/community/channels/${channelCode}/chats/${id}`);
  },
  upload: async (
    channelCode: string,
    file: File,
  ): Promise<ApiResult<UploadFileResponse, UploadFileErrorResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    return await axiosClient.post(`/community/channels/${channelCode}/chats/attachment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteFile: async (
    channelCode: string,
    params: DeleteFileRequest,
  ): Promise<ApiResult<DeleteFileResponse, DeleteFileErrorResponse>> => {
    return await axiosClient.delete(`/community/channels/${channelCode}/chats/attachment`, {
      params,
    });
  },
  toggleLinkPreview: async (
    channelCode: string,
    chatId: number,
    hidePreview: boolean,
  ): Promise<ApiResult<void, unknown>> => {
    return await axiosClient.put(
      `/community/channels/${channelCode}/chats/${chatId}/toggle-link-preview`,
      { hide_preview: hidePreview },
    );
  },
  page: async (
    channelCode: string,
    chatId: number,
    signal?: AbortSignal,
  ): Promise<ApiResult<ChatPageResponse>> => {
    return await axiosClient.get(
      `/community/channels/${channelCode}/chats/${chatId}/page`,
      createAbortableConfig(signal),
    );
  },
  changes: async (
    channelCode: string,
    params: { since: string },
    signal?: AbortSignal,
  ): Promise<ApiResult<ChatChangesResponse>> => {
    return await axiosClient.get(`/community/channels/${channelCode}/chats/changes`, {
      params,
      ...createAbortableConfig(signal),
    });
  },
  search: async (
    params: ChatSearchRequest,
    signal?: AbortSignal,
  ): Promise<ApiResult<ChatSearchResponse>> => {
    const config = signal ? { signal } : {};
    return await axiosClient.post(`/community/chats/search`, { ...params }, config);
  },
  report: async (
    channelCode: string,
    chatId: number,
    params: ChatReportRequest,
  ): Promise<ApiResult<ChatReportResponse, ChatReportErrorResponse>> => {
    return await axiosClient.post(
      `/community/channels/${channelCode}/chats/${chatId}/report`,
      params,
    );
  },
  context: async (
    channelCode: string,
    chatId: number,
    signal?: AbortSignal,
  ): Promise<ApiResult<ChatContextResponse>> => {
    return await axiosClient.get(`/community/channels/${channelCode}/chats/${chatId}/context`, {
      ...createAbortableConfig(signal),
    });
  },
};
